import { useForm, useWatch } from "react-hook-form";
import { Button } from "../Button/Button";
import { useGlobalModalContext } from "./GlobalModal";
import { TextArea } from "../TextArea/TextArea";
import { Cause } from "../Cause/Cause";
import { CANCELLATION_FEE_TYPE, MODAL_TYPES } from "common/utils/consts";
import {
  useCancelOrderMutation,
  useGetOrderByIdQuery,
  useGetOrderCancelReasonsQuery,
} from "services/orderApi";
import { CustomInput } from "../Input/CustomInput";
import { useEffect } from "react";
import { getErrorMessage } from "common/utils/helpers";

const formatRub = (value: number) =>
  `${Math.round(value).toLocaleString("ru-RU")} руб.`;

export const OrderCancelationModal = () => {
  const { hideModal, showModal, store } = useGlobalModalContext();
  const { modalProps } = store;
  const { orderId } = modalProps;

  const { data: reasons } = useGetOrderCancelReasonsQuery();

  // Заказ уже в кэше RTK Query — берём из него сумму штрафа, чтобы
  // предупредить о списании до подтверждения отмены
  const { data: order } = useGetOrderByIdQuery(
    { id: orderId },
    { skip: !orderId }
  );
  const cancellationFee = order?.cancellation_fee ?? 0;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{ reasonId: string[]; message: string }>({
    defaultValues: { reasonId: [] },
  });

  const activeReasonsId = useWatch({ control, name: "reasonId" });

  const [cancelOrder, { isLoading, isSuccess, error }] =
    useCancelOrderMutation();

  const handleModalToggle = () => {
    hideModal();
  };

  useEffect(() => {
    if (isSuccess) {
      handleModalToggle();
      showModal(MODAL_TYPES.SUCCESS_MODAL, { title: "Заказ успешно отменен" });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (error) {
      handleModalToggle();
      showModal(MODAL_TYPES.ERROR_MODAL, {
        title: "Ошибка при отмене заказа",
        subTitle: getErrorMessage(error)?.data.message,
      });
    }
  }, [error]);

  const onSubmit = handleSubmit((data) => {
    cancelOrder({
      order_id: orderId,
      reason_id: data.reasonId
        .filter((reason) => reason)
        .map((reason) => +reason),
      message: data.message,
    });
  });

  return (
    <div className="relative w-[320px] overflow-y-scroll rounded bg-white p-[15px]">
      <div className="mb-[25px] text-center text-lg">Отмена заказа</div>
      {cancellationFee > 0 && (
        <div className="mb-[25px] rounded border border-red p-[10px] text-sm">
          <div className="mb-[5px] font-semibold text-red">
            {order?.cancellation_fee_type === CANCELLATION_FEE_TYPE.HALF_PRICE
              ? `Отмена менее чем за 3 часа до начала работ — штраф ${formatRub(
                  cancellationFee
                )} (50% стоимости заказа)`
              : `Отмена за 3–24 часа до начала работ — штраф ${formatRub(
                  cancellationFee
                )} (один час работы техники)`}
          </div>
          <div className="text-gray">
            Штраф удержится из заблокированных на карте средств, остальное
            вернётся
          </div>
        </div>
      )}
      <form onSubmit={onSubmit}>
        <div>
          <span
            className={`mb-[10px] block text-sm font-semibold ${
              !!errors.reasonId ? "text-red" : "text-gray"
            }`}
          >
            Причина
          </span>

          <ul className="mb-[5px] flex flex-wrap">
            {reasons?.map((reason, i) => (
              <li key={reason.id}>
                <CustomInput
                  type="checkbox"
                  {...register(`reasonId.${i}`, {
                    validate: (v) =>
                      activeReasonsId.filter((el) => el).length > 0,
                  })}
                  value={reason.id}
                  className="border-none p-0"
                  innerelement={
                    <Cause
                      className={`${
                        activeReasonsId.includes(String(reason.id))
                          ? "bg-primary text-white"
                          : ""
                      }`}
                    >
                      {reason.name}
                    </Cause>
                  }
                />
              </li>
            ))}
          </ul>
        </div>
        <TextArea
          {...register("message", { required: true })}
          invalid={!!errors.message}
          className="mb-[25px] h-[125px] w-full resize-none"
          placeholder="Причина"
          label="По какой причине вы хотите остановить выполнение заказа"
        />
        <Button className="w-full" disabled={isLoading}>
          Завершить заказ
        </Button>
      </form>
    </div>
  );
};
